import { useActiveTags } from 'hooks/filterHooks/filterHooks'
import React, { ReactElement, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import filterStore from 'store/filter/filter'
import uiConfigStore from 'store/uiConfig/uiConfig'
import ErrorFallback from 'ui/components/ErrorFallback/ErrorFallback'
import FilterComponent from 'ui/components/Filter/FilterComponent'
import UiConfigComponent from 'ui/uiConfig/UiConfigComponent'
import sentry from 'utils/sentry/sentry'
import { useSnapshot } from 'valtio'
import UiConfigDataFlowPanel from '../UiConfigDataFlowPanel/UiConfigDataFlowPanel'
import UiConfigResolutionInspector from '../UiConfigResolutionInspector/UiConfigResolutionInspector'
import styles from './UiConfigPreview.module.less'

export type PreviewTab = 'preview' | 'resolution' | 'dataflow'

type Props = {
  activeTab?: PreviewTab
  onChangeTab?: (tab: PreviewTab) => void
  onEnterEditor?: () => void
}

export default function UiConfigPreview({ activeTab, onChangeTab, onEnterEditor }: Props): ReactElement {
  const uiConfigSnap = useSnapshot(uiConfigStore)
  const filterSnap = useSnapshot(filterStore)
  const uid = uiConfigSnap.editingUid
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState<PreviewTab>('preview')
  const currentActiveTab = activeTab ?? uncontrolledActiveTab

  const { activeTags } = useActiveTags({ availableFiltersSubset: filterSnap.tagsPerUiconfig })

  function handleChangeTab(nextTab: PreviewTab): void {
    onChangeTab?.(nextTab)
    if (activeTab == null) {
      setUncontrolledActiveTab(nextTab)
    }
  }

  if (uid == null) {
    return <div className={styles.UiConfigPreview} />
  }

  return (
    <div className={styles.UiConfigPreview}>
      <div className={styles.UiConfigPreview_TabBar}>
        <div className={styles.UiConfigPreview_SegmentedControl}>
          <button
            type="button"
            className={`${styles.UiConfigPreview_TabButton} ${currentActiveTab === 'preview' ? styles.UiConfigPreview_TabButton__active : ''}`}
            onClick={() => handleChangeTab('preview')}
            aria-pressed={currentActiveTab === 'preview'}
            title="Render the selected UI config as users currently see it"
          >
            Preview
          </button>
          <button
            type="button"
            className={`${styles.UiConfigPreview_TabButton} ${currentActiveTab === 'resolution' ? styles.UiConfigPreview_TabButton__active : ''}`}
            onClick={() => handleChangeTab('resolution')}
            aria-pressed={currentActiveTab === 'resolution'}
            title="Inspect how the raw config is transformed step by step"
          >
            Resolution chain
          </button>
          <button
            type="button"
            className={`${styles.UiConfigPreview_TabButton} ${currentActiveTab === 'dataflow' ? styles.UiConfigPreview_TabButton__active : ''}`}
            onClick={() => handleChangeTab('dataflow')}
            aria-pressed={currentActiveTab === 'dataflow'}
            title="Inspect how resolved items map to dataset instructions and where mismatches exist"
          >
            Data flow
          </button>
        </div>
        {onEnterEditor ? (
          <button
            type="button"
            className={styles.UiConfigPreview_EditButton}
            onClick={onEnterEditor}
            title="Edit the selected raw UI config JSON"
          >
            Edit JSON
          </button>
        ) : null}
      </div>
      <div className={styles.UiConfigPreview_Content}>
        {currentActiveTab === 'preview' ? (
          <div className={styles.UiConfigPreview_PreviewPane}>
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error) => sentry.captureBoundaryException(error, { boundaryName: 'UiConfigPreview' })}
            >
              <FilterComponent availableFiltersSubset={filterSnap.tagsPerUiconfig} activeTags={activeTags} />
              <div className={styles.UiConfigPreview_PreviewContent}>
                <UiConfigComponent uid={uid} />
              </div>
            </ErrorBoundary>
          </div>
        ) : currentActiveTab === 'resolution' ? (
          <UiConfigResolutionInspector uid={uid} />
        ) : (
          <UiConfigDataFlowPanel uid={uid} />
        )}
      </div>
    </div>
  )
}
